import {
  SearchableSelect,
  SearchableSelectProps,
} from '@app/components/Fields/SearchableSelect';
import { getUsersAdmin } from '@app/services/user';
import { useDebounce } from '@app/utils/use-debounce';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

type Props = Pick<
  SearchableSelectProps,
  'onChange' | 'value' | 'sizeStyle' | 'className' | 'placeholder' | 'name'
>;

export const UsersSelect = (props: Props) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const {
    data: usersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isUsersLoading,
  } = useInfiniteQuery({
    queryKey: ['users', 'admin', 'infinite', { search: debouncedSearch }],
    queryFn: ({ pageParam = 1 }) =>
      getUsersAdmin(pageParam, 20, debouncedSearch || undefined),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil((lastPage?.total || 0) / lastPage.perPage);
      const nextPage =
        lastPage.page < totalPages ? lastPage.page + 1 : undefined;

      return nextPage;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
  });

  const userOptions = (usersData?.pages || []).flatMap((page) =>
    (page?.data || []).map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    }))
  );

  return (
    <SearchableSelect
      {...props}
      className={props.className || 'w-[300px]'}
      fetchNextPage={fetchNextPage}
      hasMore={!!hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isUsersLoading}
      onSearch={(value) => setSearch(value)}
      options={userOptions}
      placeholder={props.placeholder || 'All Users'}
      sizeStyle={props.sizeStyle || 'sm'}
    />
  );
};
